import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const EASE = [0.22, 1, 0.36, 1];
const SANS = '"Geist", "Inter", system-ui, sans-serif';
const MONO = '"Geist Mono", ui-monospace, monospace';
const GRAIN_SVG = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='300'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='300' height='300' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`;

const SESSION_KEY = 'nv_support_chat_history';

const QUICK_QUESTIONS = [
  'How is my NIL Score calculated?',
  'How do I connect Instagram?',
  'What does Pro plan include?',
  'How does the Contract Analyzer work?',
];

function stripMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^#{1,3}\s+/gm, '')
    .trim();
}

function TypingBubble() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '10px 13px', background: '#141421', borderRadius: '14px 14px 14px 4px', width: 'fit-content', maxWidth: 80 }}>
      {[0, 1, 2].map(i => (
        <motion.div key={i} animate={{ opacity: [0.25, 1, 0.25], y: [0, -3, 0] }} transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.18, ease: 'easeInOut' }}
          style={{ width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.45)' }}
        />
      ))}
    </div>
  );
}

export default function SupportChatPanel({ isOpen, onClose }) {
  const { apiCall } = useAuth();
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(messages)); } catch {}
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); inputRef.current?.focus(); }, 120);
    }
  }, [isOpen, messages.length]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput('');
    setSending(true);

    const userMsg = { role: 'user', content: msg };
    const next = [...messages, userMsg];
    setMessages(next);

    try {
      const res = await apiCall('/api/support-chat', {
        method: 'POST',
        body: JSON.stringify({ message: msg, history: messages }),
      });
      setMessages([...next, { role: 'assistant', content: res?.response || 'Something went wrong — try again.' }]);
    } catch {
      setMessages([...next, { role: 'assistant', content: 'Connection error. Please try again or email info@nilvisionpro.com.' }]);
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const showQuickQuestions = messages.length === 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — only on mobile */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', display: 'block' }}
            className="nv-support-backdrop"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 32 }}
            style={{
              position: 'fixed',
              bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
              right: 16,
              zIndex: 999,
              width: 'min(360px, calc(100vw - 32px))',
              maxHeight: '65vh',
              display: 'flex',
              flexDirection: 'column',
              background: '#0D0D16',
              border: '1px solid rgba(255,255,255,0.09)',
              borderRadius: 20,
              boxShadow: '0 20px 70px rgba(0,0,0,0.8), 0 0 0 1px rgba(90,56,253,0.08)',
              overflow: 'hidden',
              fontFamily: SANS,
            }}
          >
            {/* Grain */}
            <div style={{ position: 'absolute', inset: 0, opacity: 0.04, mixBlendMode: 'overlay', backgroundImage: GRAIN_SVG, backgroundSize: '120px 120px', pointerEvents: 'none', zIndex: 0 }} />
            {/* Top hairline */}
            <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: 'linear-gradient(90deg, transparent, rgba(90,56,253,0.55) 40%, rgba(167,139,250,0.65) 60%, transparent)', pointerEvents: 'none', zIndex: 1 }} />

            {/* Content */}
            <div style={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 30, height: 30, borderRadius: 9, background: 'linear-gradient(135deg, #5A38FD, #7F00FF)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 12px rgba(90,56,253,0.4)' }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>NILVISION Support</div>
                    <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.35)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <motion.div animate={{ opacity: [1, 0.3, 1] }} transition={{ repeat: Infinity, duration: 2.2 }} style={{ width: 5, height: 5, borderRadius: '50%', background: '#4ade80', flexShrink: 0 }} />
                      AI-powered · Usually instant
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {messages.length > 0 && (
                    <motion.button whileTap={{ scale: 0.9 }} onClick={() => { setMessages([]); try { sessionStorage.removeItem(SESSION_KEY); } catch {} }}
                      title="New conversation"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: 5, cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex' }}>
                      {/* Home icon */}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                    </motion.button>
                  )}
                <motion.button whileTap={{ scale: 0.9 }} onClick={onClose} style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 7, padding: 5, cursor: 'pointer', color: 'rgba(255,255,255,0.4)', display: 'flex' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </motion.button>
                </div>
              </div>

              {/* Messages */}
              <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '14px 14px 6px', display: 'flex', flexDirection: 'column', gap: 10 }}>

                {/* Welcome state */}
                {showQuickQuestions && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.4 }}>
                    <div style={{ padding: '10px 13px', background: '#141421', borderRadius: '14px 14px 14px 4px', maxWidth: '85%' }}>
                      <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.78)', lineHeight: 1.5 }}>
                        Hi! I'm the NILVISION support assistant. Ask me anything about your NIL Score, contracts, plans, or Instagram setup.
                      </p>
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {QUICK_QUESTIONS.map(q => (
                        <motion.button key={q} whileTap={{ scale: 0.97 }} onClick={() => send(q)}
                          style={{ textAlign: 'left', padding: '8px 12px', background: 'rgba(90,56,253,0.08)', border: '1px solid rgba(90,56,253,0.2)', borderRadius: 10, fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: SANS, lineHeight: 1.3 }}>
                          {q}
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Chat history */}
                {messages.map((msg, idx) => (
                  <motion.div key={idx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      padding: '9px 13px', borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                      background: msg.role === 'user' ? 'linear-gradient(135deg, #5A38FD, #7F00FF)' : '#141421',
                      maxWidth: '86%', fontSize: '0.8rem', color: '#fff', lineHeight: 1.55,
                      boxShadow: msg.role === 'user' ? '0 0 12px rgba(90,56,253,0.25)' : 'none',
                      whiteSpace: 'pre-wrap',
                    }}>
                      {msg.role === 'assistant' ? stripMarkdown(msg.content) : msg.content}
                    </div>
                  </motion.div>
                ))}

                {/* Typing indicator */}
                {sending && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <TypingBubble />
                  </motion.div>
                )}

                <div ref={bottomRef} />
              </div>

              {/* Email escalation */}
              <div style={{ padding: '0 14px 6px', textAlign: 'center' }}>
                <a href="mailto:info@nilvisionpro.com?subject=NILVISION Support Request"
                  style={{ fontSize: '0.63rem', color: 'rgba(255,255,255,0.22)', textDecoration: 'none', fontFamily: MONO }}>
                  Still need help? <span style={{ color: 'rgba(90,56,253,0.7)', textDecoration: 'underline' }}>Email us →</span>
                </a>
              </div>

              {/* Input */}
              <div style={{ padding: '8px 12px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={handleKey}
                  placeholder="Ask a question…"
                  rows={1}
                  disabled={sending}
                  style={{
                    flex: 1, background: '#141421', border: '1px solid rgba(255,255,255,0.09)',
                    borderRadius: 12, padding: '9px 12px', color: '#fff', fontFamily: SANS,
                    fontSize: '0.8rem', resize: 'none', outline: 'none', lineHeight: 1.4,
                    maxHeight: 90, overflowY: 'auto',
                    '::placeholder': { color: 'rgba(255,255,255,0.25)' },
                  }}
                />
                <motion.button
                  whileTap={{ scale: 0.9 }}
                  onClick={() => send()}
                  disabled={!input.trim() || sending}
                  style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: input.trim() && !sending ? 'linear-gradient(135deg, #5A38FD, #7F00FF)' : 'rgba(255,255,255,0.06)',
                    border: 'none', cursor: input.trim() && !sending ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 200ms ease',
                    boxShadow: input.trim() && !sending ? '0 0 12px rgba(90,56,253,0.3)' : 'none',
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={input.trim() && !sending ? '#fff' : 'rgba(255,255,255,0.25)'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
